import React from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useQuery } from 'react-query'
import { apiService } from '../../services/api'
import { useIsMobile } from '../../hooks/useMediaQuery'
import NewEditor from '../admin/NewEditor'
import MobileContentEditor from './MobileContentEditor'
import LoadingSpinner from '../common/LoadingSpinner'

/**
 * The client "Edit my site" route.
 *
 * On phones the drag-canvas / code editor (NewEditor) is unusable, so clients
 * get the structured drill-down form editor (MobileContentEditor). Both edit the
 * SAME data: the site's `meta_data.variables`, which fills the `{{placeholder}}`
 * variables in the page templates that are actually published (see backend
 * customDomainRouter). So a phone edit and a desktop edit converge on the same
 * source of truth.
 *
 * Desktop / tablet (> 768px) keep the full visual editor.
 */
export default function MobileAwareEditor() {
  const isMobile = useIsMobile()
  if (!isMobile) return <NewEditor />
  return <MobileFormEditor />
}

function MobileFormEditor() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const siteParam = searchParams.get('site')

  // Resolve the site to edit: the explicit ?site= param, otherwise the client's
  // first site (the sidebar "Edit my site" link carries no param).
  const { data: sitesData, isLoading: sitesLoading } = useQuery(
    ['sites'],
    () => apiService.get('/sites').then(r => r.data),
    { enabled: !siteParam, refetchOnWindowFocus: false }
  )
  const { data: profile } = useQuery(
    ['client-profile'],
    () => apiService.get('/clients/profile').then(r => r.data),
    { refetchOnWindowFocus: false }
  )

  const sites = sitesData?.sites || []
  const siteId = siteParam || sites[0]?.id

  if (!siteParam && sitesLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60dvh' }}>
        <LoadingSpinner size="lg" text="Loading editor…" />
      </div>
    )
  }

  if (!siteId) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60dvh', gap: 14, padding: 24, textAlign: 'center' }}>
        <p className="text-muted" style={{ margin: 0 }}>You don't have a site to edit yet.</p>
        <button className="btn btn-primary btn-sm" onClick={() => navigate('/client/sites')}>Go to My sites</button>
      </div>
    )
  }

  // The mobile editor manages its own full-height layout (sticky top bar +
  // scrolling section list / detail), so it's rendered edge-to-edge here.
  return (
    <MobileContentEditor
      siteId={siteId}
      clientId={profile?.id}
      onBack={() => navigate('/client/sites')}
    />
  )
}
